<template>
  <div class="table-columns">
    <draggable
        v-model="localFields"
        :group="{ name: 'table-columns' }"
        handle=".drag-handle"
        :animation="150"
        :force-fallback="true"
        ghost-class="is-ghost"
        chosen-class="is-chosen"
        drag-class="is-dragging"
        @start="onDragStart"
        @end="onDragEnd"
        @change="emitFields">

      <transition-group type="transition" name="flip-list">
        <div
            v-for="(f, i) in localFields"
            :key="`col-${f.key}-${i}`"
            class="mb-50 column-row">

          <b-input-group class="input-group-merge">
            <b-input-group-prepend
                is-text
                class="drag-handle cursor-move"
                :class="{ 'is-disabled': f.required || f.locked }"
                :title="(f.required || f.locked) ? 'Locked column' : 'Drag to reorder'">
              <b-icon-grip-vertical />
            </b-input-group-prepend>

            <div class="form-control d-flex flex-grow-1 justify-content-between px-0">
              <span class="text-truncate">{{ f.label }}</span>

              <b-form-checkbox
                  v-model="f.visible"
                  :value="true"
                  :unchecked-value="false"
                  :disabled="f.required"
                  switch
                  class="mb-0"
                  @input="emitFields"
              />
            </div>
          </b-input-group>
        </div>
      </transition-group>
    </draggable>

    <div class="mt-1 d-flex flex-column w-100">
      <div class="d-flex justify-content-between gap-1 mb-50">
        <b-button size="sm" variant="outline-primary" class="w-100" @click="showAll">
          <b-icon icon="eye" class="mr-25" /> Show all
        </b-button>

        <b-button size="sm" variant="outline-danger" class="w-100" @click="hideAll">
          <b-icon icon="eye-slash" class="mr-25" /> Hide all
        </b-button>
      </div>

      <b-button
          v-if="originalFields.length"
          size="sm"
          variant="outline-secondary"
          class="mb-50"
          block
          @click="reset">
        <b-icon icon="arrow-counterclockwise" class="mr-25" /> Reset
      </b-button>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

export default {
  name: 'TableColumns',
  components: { draggable },
  props: {
    // v-model: your table fields array
    value: { type: Array, default: () => [] },
  },
  data() {
    const initial = this.cloneFields(this.value)
    return {
      originalFields: initial,
      localFields: this.onlyLabeled(initial), // show labeled only
      isDragging: false,
    }
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        // keep local in sync if parent overwrites fields (load settings / etc.)
        const cloned = this.cloneFields(newVal)
        this.localFields = this.onlyLabeled(cloned)
      },
    },
  },
  methods: {
    onlyLabeled(fields) {
      return (fields || []).filter(f => !!f?.label)
    },

    cloneFields(fields) {
      // clone enough to allow reorder/toggle without mutating props
      return (fields || []).map(f => ({
        ...f,
        visible: f?.visible !== false, // default true
      }))
    },

    onDragStart() {
      this.isDragging = true
    },
    onDragEnd() {
      this.isDragging = false
      // emitPlan already fires on @change, but @end is a safe backstop
      this.emitFields()
    },

    emitFields() {
      // Important: you said you only want to *display* labeled fields.
      // But emitting only labeled fields would DROP unlabeled fields from the parent array.
      // So we merge changes back into the full original prop array shape.

      const byKey = new Map((this.localFields || []).map(f => [String(f.key), f]))

      const out = (this.value || []).map(f => {
        const patched = byKey.get(String(f.key))
        return patched ? { ...f, ...patched } : { ...f }
      })

      // Also: order changes only apply to the labeled subset.
      // If you want unlabeled fields preserved at the end, we can rebuild order:
      const labeledKeysInOrder = (this.localFields || []).map(f => String(f.key))
      const labeled = []
      const unlabeled = []

      for (const f of out) {
        if (f?.label) labeled.push(f)
        else unlabeled.push(f)
      }

      const labeledByKey = new Map(labeled.map(f => [String(f.key), f]))
      const orderedLabeled = labeledKeysInOrder.map(k => labeledByKey.get(k)).filter(Boolean)

      const finalOut = [...orderedLabeled, ...unlabeled]

      this.$emit('input', finalOut.map(f => ({ ...f })))
      this.$emit('change', finalOut.map(f => ({ ...f })))
    },

    showAll() {
      this.localFields.forEach(f => { f.visible = true })
      this.emitFields()
    },

    hideAll() {
      this.localFields.forEach(f => { f.visible = !!f.required })
      this.emitFields()
    },

    reset() {
      // reset to original snapshot (order + visible) for labeled fields
      const resetLocal = this.onlyLabeled(this.cloneFields(this.originalFields))
      this.localFields = resetLocal
      this.emitFields()
    },
  },
}
</script>

<style scoped>
.cursor-move { cursor: move; }
.drag-handle.is-disabled { opacity: 0.45; }
.is-ghost { opacity: 0.35; }
.is-chosen { opacity: 0.8; }
.flip-list-move { transition: transform 150ms; }
</style>
